import { useState, useEffect, ChangeEvent } from "react";
import { Box, FormControlLabel, Checkbox } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import MainHoursConfigTable from "../mainHours/MainHoursConfigTable";
import useTextTranslate from "../../../hooks/useTextTranslate";
import styles from "./styles";
import useCss from "../../../../../hooks/useCss";

interface FormValues {
  hoursName: string;
  description: string;
  hoursConfig: Record<string, unknown>[];
  showForManagers: boolean;
  readOnly: boolean;
}

interface Props {
  memoDataset: (data: Record<string, unknown>) => void;
  isManager?: boolean;
}

const MainHoursAdd = ({ memoDataset, isManager }: Props) => {
  const classes = useCss(styles);
  const hoursNamePlaceholder = useTextTranslate("Enter hours name");
  const descriptionPlaceholder = useTextTranslate("Enter description");

  const initFormValues: FormValues = {
    hoursName: "",
    description: "",
    hoursConfig: [],
    showForManagers: false,
    readOnly: false,
  };

  const [formValue, setFormValue] = useState<FormValues>(initFormValues);
  const [hoursNameError, sethoursNameError] = useState(false);

  useEffect(() => {
    memoDataset(initFormValues as unknown as Record<string, unknown>);
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const data = { ...formValue, [name]: value };

    setFormValue(data);
    name === "hoursName" && sethoursNameError(value.length < 1 ? true : false);

    memoDataset({ [name]: value });
  };

  const handleCheckboxChange =
    (property: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const updatedValues = { ...formValue, [property]: checked };

      if (property === "showForManagers" && !checked) {
        updatedValues.readOnly = false;
      }

      setFormValue(updatedValues);
      // Only send the changed fields — NOT the full formValue which would
      // overwrite hoursConfig managed independently by MainHoursConfigTable.
      const allValues = updatedValues as Record<string, unknown>;
      const { hoursConfig: _ignored, ...partialUpdate } = allValues;
      memoDataset(partialUpdate);
    };

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      className={classes.formControl}
    >
      <SbuxTextField
        isError={hoursNameError}
        error={hoursNameError}
        label={<SbuxTranslate>{"Hours Name"}</SbuxTranslate>}
        placeholder={hoursNamePlaceholder}
        handleChange={handleChange}
        value={formValue.hoursName}
        name={"hoursName"}
        required
        errorMessage={
          hoursNameError ? (
            <SbuxTranslate>{"At least one character required"}</SbuxTranslate>
          ) : (
            " "
          )
        }
      />
      <SbuxTextField
        label={<SbuxTranslate>{"Description"}</SbuxTranslate>}
        placeholder={descriptionPlaceholder}
        handleChange={handleChange}
        value={formValue.description}
        name={"description"}
        preventAutoFocus
      />

      <Box>
        <FormControlLabel
          className="disabledLabel"
          control={
            <Checkbox
              className="checkboxStyle"
              inputProps={{ "aria-label": "Manager Access" }}
              disableFocusRipple
              disableRipple
              checked={formValue.showForManagers}
              onChange={handleCheckboxChange("showForManagers")}
              disabled={isManager}
            />
          }
          label={<SbuxTranslate>{"Manager Access"}</SbuxTranslate>}
        />
      </Box>
      <Box>
        {formValue.showForManagers && (
          <FormControlLabel
            className="disabledLabel"
            control={
              <Checkbox
                className="readOnlyCheckboxStyle"
                inputProps={{ "aria-label": "Manager Read-only" }}
                disableFocusRipple
                disableRipple
                checked={formValue.readOnly}
                onChange={handleCheckboxChange("readOnly")}
                disabled={isManager}
              />
            }
            label={<SbuxTranslate>{"Manager Read-only"}</SbuxTranslate>}
          />
        )}
      </Box>

      {/* Hours Config Table */}
      <Box className="fullWidth">
        <MainHoursConfigTable rowData={{}} memoDataset={memoDataset} />
      </Box>
    </Box>
  );
};

export default MainHoursAdd;
